import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 19 — coach Feedback/Messages/Files/Library
 * cross-Workspace workbenches + CoachNote.
 * Mirrors feedbackQueries.js/messagingQueries.js/fileQueries.js's
 * `request()` error contract exactly (`.status`/`.body` on thrown errors).
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });
const jsonPatch = (path, payload) => request(path, { method: "PATCH", body: JSON.stringify(payload ?? {}) });
const jsonDelete = (path) => request(path, { method: "DELETE" });

// -- Feedback workbench -------------------------------------------------------
export function useCoachFeedbackWorkbench() {
  return useQuery({ queryKey: ["coach-feedback-workbench"], queryFn: () => request("/api/rehearsal-room/coach/feedback") });
}

// -- Messages workbench -------------------------------------------------------
export function useCoachMessagesWorkbench(q = "") {
  return useQuery({
    queryKey: ["coach-messages-workbench", q],
    queryFn: () => request(`/api/rehearsal-room/coach/messages${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  });
}

export function useFlagThreadFollowup(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, reason }) => jsonPost(`/api/rehearsal-room/coach/messages/${workspaceId}/threads/${threadId}/flag-followup`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-messages-workbench"] }),
  });
}

// -- Files workbench -----------------------------------------------------------
export function useCoachFilesWorkbench() {
  return useQuery({ queryKey: ["coach-files-workbench"], queryFn: () => request("/api/rehearsal-room/coach/files") });
}

// -- The coach's own Workspaces (for a `client_specific` scope/fork picker) ---
export function useCoachOwnWorkspaces() {
  return useQuery({ queryKey: ["coach-own-workspaces"], queryFn: () => request("/api/rehearsal-room/coach/workspaces/mine") });
}

// -- Library workbench (minimal CRUD) ------------------------------------------
export function useCoachLibraryWorkbench(includeArchived = false) {
  return useQuery({
    queryKey: ["coach-library-workbench", includeArchived],
    queryFn: () => request(`/api/rehearsal-room/coach/library${includeArchived ? "?include_archived=true" : ""}`),
  });
}

function useInvalidateLibrary() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["coach-library-workbench"] });
}

export function useCreateLibraryItemFromFile() {
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: (payload) => jsonPost("/api/rehearsal-room/coach/library/from-file", payload),
    onSuccess: invalidate,
  });
}

export function useUpdateLibraryItem() {
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: ({ libId, ...payload }) => jsonPatch(`/api/rehearsal-room/coach/library/${libId}`, payload),
    onSuccess: invalidate,
  });
}

export function usePublishLibraryVersion() {
  const invalidate = useInvalidateLibrary();
  return useMutation({
    mutationFn: ({ libId, libvId }) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/versions/${libvId}/publish`),
    onSuccess: invalidate,
  });
}

export function useArchiveLibraryItem() {
  const invalidate = useInvalidateLibrary();
  return useMutation({ mutationFn: (libId) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/archive`), onSuccess: invalidate });
}

export function useUnarchiveLibraryItem() {
  const invalidate = useInvalidateLibrary();
  return useMutation({ mutationFn: (libId) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/unarchive`), onSuccess: invalidate });
}

// Prompt 30D — permanent delete, only ever offered once a LibraryItem
// is already archived (see coach_workbench_routes.delete_library_item_route).
export function useDeleteLibraryItem() {
  const invalidate = useInvalidateLibrary();
  return useMutation({ mutationFn: (libId) => request(`/api/rehearsal-room/coach/library/${libId}`, { method: "DELETE" }), onSuccess: invalidate });
}

// -- Master v5 Prompt 20 — typed catalog + lifecycle ---------------------------
export function useLibraryCatalog(filters = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
  const qs = params.toString();
  return useQuery({
    queryKey: ["coach-library-catalog", qs],
    queryFn: () => request(`/api/rehearsal-room/coach/library/catalog${qs ? `?${qs}` : ""}`),
  });
}

export function useLibraryFavoritesList() {
  return useQuery({ queryKey: ["coach-library-favorites"], queryFn: () => request("/api/rehearsal-room/coach/library/favorites") });
}

export function useLibraryRecentUsage(libId) {
  return useQuery({
    queryKey: ["coach-library-recent-usage", libId],
    queryFn: () => request(`/api/rehearsal-room/coach/library/${libId}/recent-usage`),
    enabled: !!libId,
  });
}

function useInvalidateCatalog() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["coach-library-workbench"] });
    qc.invalidateQueries({ queryKey: ["coach-library-catalog"] });
    qc.invalidateQueries({ queryKey: ["coach-library-favorites"] });
  };
}

export function useCreateNativeLibraryItem() {
  const invalidate = useInvalidateCatalog();
  return useMutation({ mutationFn: (payload) => jsonPost("/api/rehearsal-room/coach/library", payload), onSuccess: invalidate });
}

export function useUpdateLibraryVersionMetadata() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ libId, libvId, ...payload }) => jsonPatch(`/api/rehearsal-room/coach/library/${libId}/versions/${libvId}`, payload),
    onSuccess: invalidate,
  });
}

export function useCreateNextDraftVersion() {
  const invalidate = useInvalidateCatalog();
  return useMutation({ mutationFn: (libId) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/versions/next-draft`), onSuccess: invalidate });
}

export function useSubmitLibraryVersionForReview() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ libId, libvId }) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/versions/${libvId}/submit-review`),
    onSuccess: invalidate,
  });
}

export function useReturnLibraryVersionToDraft() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ libId, libvId }) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/versions/${libvId}/return-to-draft`),
    onSuccess: invalidate,
  });
}

export function useRetireLibraryVersion() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ libId, libvId }) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/versions/${libvId}/retire`),
    onSuccess: invalidate,
  });
}

export function useDuplicateLibraryItem() {
  const invalidate = useInvalidateCatalog();
  return useMutation({ mutationFn: (libId) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/duplicate`), onSuccess: invalidate });
}

export function useForkLibraryItemToWorkspace() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ libId, workspaceId }) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/fork`, { workspace_id: workspaceId }),
    onSuccess: invalidate,
  });
}

export function useToggleLibraryFavorite() {
  const invalidate = useInvalidateCatalog();
  return useMutation({ mutationFn: (libId) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/favorite`), onSuccess: invalidate });
}

export function useLibraryAssets(libId) {
  return useQuery({
    queryKey: ["coach-library-assets", libId],
    queryFn: () => request(`/api/rehearsal-room/coach/library/${libId}/assets`),
    enabled: !!libId,
  });
}

export function useAddLibraryAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ libId, ...payload }) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/assets`, payload),
    onSuccess: (_data, { libId }) => qc.invalidateQueries({ queryKey: ["coach-library-assets", libId] }),
  });
}

export function useRemoveLibraryAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ libId, lbaId }) => jsonDelete(`/api/rehearsal-room/coach/library/${libId}/assets/${lbaId}`),
    onSuccess: (_data, { libId }) => qc.invalidateQueries({ queryKey: ["coach-library-assets", libId] }),
  });
}

export function useLibraryLinks(libId) {
  return useQuery({
    queryKey: ["coach-library-links", libId],
    queryFn: () => request(`/api/rehearsal-room/coach/library/${libId}/links`),
    enabled: !!libId,
  });
}

export function useCreateLibraryLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ libId, toLibId }) => jsonPost(`/api/rehearsal-room/coach/library/${libId}/links`, { to_lib_id: toLibId }),
    onSuccess: (_data, { libId }) => qc.invalidateQueries({ queryKey: ["coach-library-links", libId] }),
  });
}

export function useDeleteLibraryLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lnkId }) => jsonDelete(`/api/rehearsal-room/coach/library/links/${lnkId}`),
    onSuccess: (_data, { libId }) => qc.invalidateQueries({ queryKey: ["coach-library-links", libId] }),
  });
}

// -- CoachNote — always coach-private ------------------------------------------
export function useCoachNotes(memberId) {
  return useQuery({
    queryKey: ["coach-notes", memberId || null],
    queryFn: () => request(`/api/rehearsal-room/coach/notes${memberId ? `?member_id=${memberId}` : ""}`),
  });
}

function useInvalidateNotes() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["coach-notes"] });
}

export function useCreateCoachNote() {
  const invalidate = useInvalidateNotes();
  return useMutation({ mutationFn: (payload) => jsonPost("/api/rehearsal-room/coach/notes", payload), onSuccess: invalidate });
}

export function useUpdateCoachNote() {
  const invalidate = useInvalidateNotes();
  return useMutation({
    mutationFn: ({ cntId, bodyText }) => jsonPatch(`/api/rehearsal-room/coach/notes/${cntId}`, { body_text: bodyText }),
    onSuccess: invalidate,
  });
}

export function useDeleteCoachNote() {
  const invalidate = useInvalidateNotes();
  return useMutation({ mutationFn: (cntId) => request(`/api/rehearsal-room/coach/notes/${cntId}`, { method: "DELETE" }), onSuccess: invalidate });
}
